import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BrandIntegrityService } from "@/services/agent/BrandIntegrityService";
import { Agent } from "@/types/agent/agent";
import {
  CreateCustomTestParams,
  RunElevenLabsTestParams,
  RunFullAuditParams,
  RunPhoneCheckParams,
  SaveManualTestParams,
} from "@/types/agent/brand-integrity";

/**
 * Hook to fetch available test categories for ElevenLabs testing
 */
export const useTestCategories = () => {
  return useQuery({
    queryKey: ["brand-test-categories"],
    queryFn: () => BrandIntegrityService.getTestCategories(),
    staleTime: 1000 * 60 * 60, // Categories are static, cache for an hour
  });
};

/**
 * Hook to run brand tests using ElevenLabs simulate-conversation API
 */
export const useRunElevenLabsTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RunElevenLabsTestParams) => BrandIntegrityService.runElevenLabsTest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["brand-test-runs-infinite"],
      });
    },
  });
};

/**
 * Hook to fetch brand test runs with filtering and pagination
 */
export const useBrandTestRuns = (options: {
  page?: number;
  pageSize?: number;
  agentId?: string;
  statusFilter?: "all" | "passed" | "failed";
  dateRange?: string;
  agents: Agent[];
  runTypes?: string[];
  excludeRunTypes?: string[];
  enabled?: boolean;
}) => {
  const { enabled = true, ...queryOptions } = options;

  return useInfiniteQuery({
    queryKey: [
      "brand-test-runs-infinite",
      queryOptions.agentId,
      queryOptions.statusFilter,
      queryOptions.dateRange,
      queryOptions.runTypes,
      queryOptions.excludeRunTypes,
    ],
    queryFn: ({ pageParam = 1 }) =>
      BrandIntegrityService.getBrandTestRuns({
        ...queryOptions,
        page: pageParam,
        pageSize: queryOptions.pageSize || 20,
      }),
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage.data.length === (queryOptions.pageSize || 20);
      return hasNextPage ? lastPage.page + 1 : undefined;
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes (was 10 seconds)
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
  });
};

/**
 * Hook to fetch results for a specific test run (lazy loading)
 */
export const useBrandTestResults = (testRunId: string | null) => {
  return useQuery({
    queryKey: ["brand-test-results", testRunId],
    queryFn: () => (testRunId ? BrandIntegrityService.getBrandTestResults(testRunId) : Promise.reject()),
    enabled: !!testRunId,
    staleTime: 0, // Always consider fresh - real-time invalidation via broadcast notifications
  });
};

/**
 * Hook to fetch custom test scenarios for the current user
 */
export const useCustomTests = () => {
  return useQuery({
    queryKey: ["custom-tests"],
    queryFn: () => BrandIntegrityService.getCustomTests(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

/**
 * Hook to create a new custom test scenario
 */
export const useCreateCustomTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateCustomTestParams) => BrandIntegrityService.createCustomTest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["custom-tests"],
      });
    },
  });
};

/**
 * Hook to delete a custom test scenario
 */
export const useDeleteCustomTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (testId: string) => BrandIntegrityService.deleteCustomTest(testId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["custom-tests"],
      });
    },
  });
};

/**
 * Hook to kick off a Full Brand Audit. Returns the created testRunId via
 * onSuccess; the audit then runs entirely server-side with progress
 * broadcasts over the `brand-tests` Supabase channel.
 */
export const useRunFullAudit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: RunFullAuditParams) => BrandIntegrityService.runFullAudit(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
    },
  });
};

/**
 * Hook to kick off a phone check (handoff or transfer). Same server-side
 * flow and progress broadcasts as a Full Audit.
 */
export const useRunPhoneCheck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: RunPhoneCheckParams) => BrandIntegrityService.runPhoneCheck(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
    },
  });
};

/**
 * Hook to save a manual Quick Test conversation
 */
export const useSaveManualTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SaveManualTestParams) => BrandIntegrityService.saveManualTest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["brand-test-runs-infinite"],
      });
    },
  });
};
